import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, MessageSquareMore, Search, Filter, RefreshCw, ChevronDown, ArrowUpDown, X } from 'lucide-react';
import { Site } from '../types';
import { FormsTab } from './FormsTab';
import { FeedbackTab } from './FeedbackTab';
import { callSiteApi } from '../utils';

interface SubmissionsTabProps {
  site: Site;
  onUpdateSite?: (updates: Partial<Site>) => void;
}

type SubTab = 'forms' | 'feedback';

export function SubmissionsTab({ site }: SubmissionsTabProps) {
  const [activeTab, setActiveTab] = useState<SubTab>('feedback');
  const [searchTerm, setSearchTerm] = useState('');
  const [formFilter, setFormFilter] = useState<string>('all');
  const [showSearch, setShowSearch] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [formsList, setFormsList] = useState<Array<{id: number, title: string}>>([]);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);


  const fetchFormsList = async () => {
    try {
      const data = await callSiteApi(site.url, site.publicKey, site.secretKey, 'submissions/forms');
      if (data && data.forms) {
        setFormsList(data.forms);
      }
    } catch (error) {
      console.error('Failed to fetch forms list:', error);
    }
  };

  useEffect(() => {
    fetchFormsList();
  }, [site.id]);

  useEffect(() => {
    if (showSearch) searchRef.current?.focus();
  }, [showSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const activeFormTitle = formFilter === 'all' 
    ? 'All Forms' 
    : formsList.find(f => f.id.toString() === formFilter)?.title || 'All Forms';

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        {/* Tab Switcher */}
        <div className="flex p-1 bg-muted/50 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('feedback')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'feedback' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <MessageSquareMore className="w-4 h-4" />
            Feedback
          </button>
          <button
            onClick={() => setActiveTab('forms')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'forms' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Database className="w-4 h-4" />
            Form Submissions
          </button>
        </div>

        {/* Dynamic Header Actions */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="relative flex items-center h-10">
              <AnimatePresence initial={false}>
                {showSearch ? (
                  <motion.div
                    key="search-box"
                    initial={{ width: 40, opacity: 0.6 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    className="relative flex items-center h-10"
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <input
                      ref={searchRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search submissions..."
                      onBlur={() => { if (!searchTerm) setShowSearch(false); }}
                      className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                    />
                    <button
                      onMouseDown={(e) => { e.preventDefault(); setShowSearch(false); setSearchTerm(''); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="search-btn"
                    onClick={() => setShowSearch(true)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <Search className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {activeTab === 'forms' && (
              <div className="relative h-10" ref={filterRef}>
                <button
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                  className={`flex items-center space-x-2 px-4 h-full rounded-lg border transition-colors ${
                    formFilter !== 'all'
                      ? 'bg-background shadow-sm text-foreground border-transparent'
                      : 'bg-card border-border text-foreground hover:bg-muted'
                  }`}
                >
                  <ArrowUpDown className="w-4 h-4" />
                  <span className="text-sm font-medium whitespace-nowrap">
                    {activeFormTitle}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {showFilterMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[200px] p-1.5 z-40"
                    >
                      <button
                        onClick={() => { setFormFilter('all'); setShowFilterMenu(false); }}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${
                          formFilter === 'all' ? 'bg-background shadow-sm text-foreground' : 'text-foreground hover:bg-accent'
                        }`}
                      >
                        All Forms
                        {formFilter === 'all' && <div className="w-2 h-2 bg-current rounded-full" />}
                      </button>
                      {formsList.map((form) => (
                        <button
                          key={form.id}
                          onClick={() => { setFormFilter(form.id.toString()); setShowFilterMenu(false); }}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${
                            formFilter === form.id.toString() ? 'bg-background shadow-sm text-foreground' : 'text-foreground hover:bg-accent'
                          }`}
                        >
                          <span className="truncate pr-4">{form.title}</span>
                          {formFilter === form.id.toString() && <div className="w-2 h-2 bg-current rounded-full" />}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            <button className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Filter options">
              <Filter className="w-4 h-4" />
            </button>

            <button
              onClick={handleRefresh}
              className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
              title="Refresh submissions"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeTab}-${isRefreshing}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'forms' ? (
            <FormsTab
              site={site}
              searchTerm={searchTerm}
              formFilter={formFilter}
              refreshTrigger={isRefreshing}
            />
          ) : (
            <FeedbackTab
              site={site}
              searchTerm={searchTerm}
              refreshTrigger={isRefreshing}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
